import React, { Fragment, useCallback, useEffect, useState } from 'react';

import Image from 'next/legacy/image';
import Link from 'next/link';

import { Team } from '@repo/domain/team';
import { apiErrorToMessage } from '@repo/editor/src/global/apiErrorToMessage';
import { FREE_PLAN_MAX_MEMBER_COUNT } from '@repo/editor/src/global/constants';
import { TeamInvite } from '@repo/editor/src/types';
import { withEventScope } from '@repo/event-processor';
import { Badge } from '@repo/ui/components/Badge';
import { Button } from '@repo/ui/components/Buttons';
import PlusIcon from '@repo/ui/components/Icon/PlusIcon';
import { TextInput, EMAIL_INVALID_MESSAGE } from '@repo/ui/components/Inputs';
import { Spacer } from '@repo/ui/components/Spacer';
import { fireToast } from '@repo/ui/components/Toast';
import { H2, H3, H5, P2 } from '@repo/ui/components/Typography';
import { ProfileImage } from '@repo/ui/deprecated/ProfileImage';
import { themeStyle } from '@repo/ui/theming';
import { buildProfileImageUrl, buildWebsiteUrl } from '@repo/utils/url';

import ChooseTeamPlanFlowModal from 'components/ChooseTeamPlanFlow/ChooseTeamPlanFlowModal';
import teamsApi from 'global/teamsApi';
import APIError from 'global/types/ApiError';
import { FlexColumn } from 'styles/layouts';

import { INVITATIONS_SENT, SEND_INVITATIONS } from './data';
import ErrorBox from './ErrorBox/ErrorBox';
import SentInvite from './SentInvite/SentInvite';
import {
  AccountButtonWrapper,
  ButtonWrapper,
  IconButtonWrapper,
  ImageWrapper,
  TruncatedTitleWrapper,
  Wrapper,
  UpgradePlanButton,
} from './styles';
import { InviteTeammemberModalContentProps } from './types';

const MAXIMUM_TEAM_SIZE = 50;

const getRemainingAvailableInvitations = (
  team: Team,
  alreadyInvited = 0
): number => {
  if (team.plan) {
    return Infinity;
  }

  return FREE_PLAN_MAX_MEMBER_COUNT - (team.memberCount + alreadyInvited);
};

const InviteTeammemberModalContent: React.FC<
  InviteTeammemberModalContentProps
> = ({ onInvintationsUpdated, team }) => {
  const [emails, setEmails] = useState<string[]>(['', '', '']);
  const [sentInvites, setSentInvites] = useState<TeamInvite[]>([]);
  const [invitationsSent, setInvitationsSent] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<(string | undefined)[]>(
    []
  );
  const [invitationsSentCount, setInvitationsSentCount] = useState(0);
  const [invitationLimitHit, setInvitationLimitHit] = useState(false);
  const [showSelectTeamPlan, setShowSelectTeamPlan] = useState(false);

  const loadInvites = useCallback(async (): Promise<string[]> => {
    const response = await teamsApi.listInvites(false);
    if (!response.success) {
      const error = apiErrorToMessage(response.error ?? '');

      fireToast({
        label: `Failed to load invites. ${error}`,
        error: true,
      });

      return [];
    }

    const emails = response.results.map(({ receiverEmail }) => receiverEmail);
    if (response.results.length === 0) {
      setEmails(['', '', '']);
      setSentInvites([]);
      setInvitationsSent(false);
      setInvitationsSentCount(0);
    } else {
      setSentInvites(response.results);
    }
    return emails;
  }, []);

  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  const handleAddAnother = (): void => {
    setEmails((prev) => [...prev, '']);
  };

  const handleSendInvitations = async (): Promise<void> => {
    if (invitationsSent) {
      setInvitationsSent(false);
      setEmails(['']);
      setErrorMessages(['']);
    } else {
      const filledEmails = emails.filter((email) => email.length > 0);
      if (filledEmails.length === 0) {
        fireToast({
          label: 'Please input at least one email-address',
          error: true,
        });
        return;
      }

      const response = await teamsApi.createInvites(filledEmails);
      const isInvitationLimitReached =
        response.error === APIError.INVITE_LIMIT_REACHED;
      setInvitationLimitHit(isInvitationLimitReached);

      if (!response.success) {
        if (isInvitationLimitReached) {
          return;
        }

        let errors: string[] = [];
        if ('issues' in response && response.issues?.length) {
          errors = emails.map((email) => {
            const index = filledEmails.findIndex(
              (filledEmail) => filledEmail === email
            );
            const issue = response.issues?.find((issue) => {
              if (
                typeof issue === 'object' &&
                Array.isArray(issue.path) &&
                issue.path.length &&
                issue.path[0] === 'body' &&
                issue.path[1] === 'emails' &&
                issue.path[2] === index
              ) {
                return true;
              }
              return false;
            });
            if (issue) {
              return EMAIL_INVALID_MESSAGE;
            }
            return '';
          });

          setErrorMessages(errors);
        }

        if (!errors.some((error) => !!error)) {
          const error = apiErrorToMessage(response.error ?? '');
          fireToast({
            label: `Failed to send invites. ${error}`,
            error: true,
          });
        }
        return;
      }

      const invites = await loadInvites();

      const sentInvites = invites.filter((email) =>
        filledEmails.find(
          (filledEmail) => filledEmail.toLowerCase() === email.toLowerCase()
        )
      );
      const sentInvitesCount = new Set(sentInvites).size;
      const filledInvitesCount = new Set(filledEmails).size;
      setInvitationsSentCount((oldCount) => sentInvitesCount + oldCount);
      if (sentInvitesCount < filledInvitesCount) {
        fireToast({
          label: `Could only create ${sentInvitesCount} of ${filledInvitesCount} invites.`,
          error: true,
        });
        setInvitationLimitHit(true);
      }
      setInvitationsSent(true);
      setEmails([]);
      setErrorMessages([]);

      if (onInvintationsUpdated) {
        onInvintationsUpdated();
      }
    }
  };

  const handleOnEmailInput =
    (index: number) =>
    (errror: string): void => {
      const newEmailsArray = emails.map((item, i) => {
        if (index === i) {
          return errror;
        }
        return item;
      });
      setEmails(newEmailsArray);
      const newErrorsArray = errorMessages.map((item, i) => {
        if (index === i) {
          return undefined;
        }
        return item;
      });
      setErrorMessages(newErrorsArray);
    };

  const handleDeleteSentInvite = (deletedEmail: string): void => {
    setSentInvites((previous) =>
      previous.filter((invite) => invite.receiverEmail !== deletedEmail)
    );
    if (onInvintationsUpdated) {
      onInvintationsUpdated();
    }
  };

  const hasUpgradablePlan = team.memberCount < MAXIMUM_TEAM_SIZE;

  return (
    <Wrapper>
      <FlexColumn alignItems="start" width="336px">
        <H2>
          {!invitationsSent ? SEND_INVITATIONS.title : INVITATIONS_SENT.title}
        </H2>
        <Spacer height="12px" />
        <AccountButtonWrapper>
          <TruncatedTitleWrapper>
            <ProfileImage
              profile={{
                ...team,
                profileImage: buildProfileImageUrl(team.profileImage),
                isTeam: true,
              }}
              size="32px"
            />
            <H3 noTextOverflow>{team.name}</H3>
          </TruncatedTitleWrapper>
          <Badge text={team.plan ?? 'FREE'} variant="blue" />
        </AccountButtonWrapper>
        {invitationsSent && (
          <>
            <Spacer height="12px" />
            <P2>New members were invited to your team.</P2>
          </>
        )}
        <Spacer height="32px" />
        {sentInvites.length > 0 && (
          <>
            <H5>{INVITATIONS_SENT.subtitle}</H5>
            <Spacer height="8px" />
            <FlexColumn gap="12px" width="100%">
              {sentInvites.map((invite) => (
                <SentInvite
                  key={invite.receiverEmail}
                  canUpdate={invite.canUpdate}
                  email={invite.receiverEmail}
                  onDelete={handleDeleteSentInvite}
                />
              ))}
            </FlexColumn>
            <Spacer height="12px" />
          </>
        )}
        {!invitationsSent && (
          <>
            <H5>{SEND_INVITATIONS.subtitle}</H5>
            <Spacer height="12px" />
            {emails.map((email, index) => (
              <Fragment key={`email-invitation-${email}-${index}`}>
                <TextInput
                  errorMessage={
                    emails[index].length > 0
                      ? (errorMessages[index] ?? undefined)
                      : undefined
                  }
                  placeholder="Email"
                  value={emails[index]}
                  onChanged={handleOnEmailInput(index)}
                />
                <Spacer height="12px" />
              </Fragment>
            ))}
            {getRemainingAvailableInvitations(
              team,
              invitationsSentCount + emails.length
            ) > 0 && (
              <IconButtonWrapper onClick={handleAddAnother}>
                <PlusIcon
                  height="12px"
                  theme={{ color: themeStyle.varInkMedium }}
                />
                <P2>Add Another</P2>
              </IconButtonWrapper>
            )}
          </>
        )}
        {invitationLimitHit && (
          <>
            <Spacer height="18px" />
            <ErrorBox styles={{ padding: 12, width: '100%' }}>
              {hasUpgradablePlan ? (
                <>
                  Invite limit reached. Upgrade your plan to invite additional
                  members to your team.
                  <br />
                  <UpgradePlanButton
                    cursor="pointer"
                    textDecoration="underline"
                    onClick={(): void => setShowSelectTeamPlan(true)}
                  >
                    Upgrade now
                  </UpgradePlanButton>
                </>
              ) : (
                <>
                  Maximum number of team members reached.
                  <br />
                  <Link href={buildWebsiteUrl('/contact')} target="_blank">
                    Contact Support
                  </Link>
                </>
              )}
            </ErrorBox>
            <ChooseTeamPlanFlowModal
              initialQuantity={5}
              isOpen={showSelectTeamPlan}
              onClose={(): void => {
                setShowSelectTeamPlan(false);
                setInvitationLimitHit(false);
              }}
            />
          </>
        )}
        {getRemainingAvailableInvitations(team, invitationsSentCount) > 0 && (
          <>
            <Spacer height="16px" />
            <ButtonWrapper>
              <Button
                label={
                  !invitationsSent
                    ? SEND_INVITATIONS.button
                    : INVITATIONS_SENT.button
                }
                name="sendInvitationsButton"
                width="100%"
                onClick={handleSendInvitations}
              />
            </ButtonWrapper>
          </>
        )}
      </FlexColumn>
      <ImageWrapper>
        <Image
          alt="Decorative illustration"
          height={400}
          src="/images/inviteTeammemberGraphic.svg"
          width={400}
        />
      </ImageWrapper>
    </Wrapper>
  );
};

export default withEventScope(InviteTeammemberModalContent, {
  module: 'inviteTeammemberModal',
});
